import cn from 'classnames';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import { Textarea } from '@/shared/ui';

import { patientAgentMessages, type ThreadApprovalItem } from '../config';
import { parseProposal } from '../lib';

import styles from './AgentChat.module.scss';
import { ProposalPreview } from './proposalPreviews';

type ApprovalCardProps = {
  disabled: boolean;
  item: ThreadApprovalItem;
  className?: string;
  /** Rendered in the composer slot while the gate blocks sending. */
  takeover?: boolean;
  onDecision: (
    proposalID: string,
    decision: 'approve' | 'disapprove' | 'feedback',
    feedback?: string,
  ) => void;
};

export const ApprovalCard: FC<ApprovalCardProps> = ({
  disabled,
  item,
  onDecision,
  className,
  takeover = false,
}) => {
  const { formatMessage } = useIntl();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const resolved = item.status !== 'pending';
  const parsed = parseProposal(item.kind, item.preview);
  const isNew =
    (parsed.kind === 'template' && !parsed.baseSeriesID) ||
    (parsed.kind === 'schema' && !parsed.schemaID);

  const closeFeedback = () => {
    setFeedbackOpen(false);
    setFeedback('');
  };

  return (
    <section
      className={cn(
        styles.approvalCard,
        takeover && styles.approvalCardTakeover,
        className,
      )}
    >
      <header className={styles.approvalHeader}>
        <span>{formatMessage(patientAgentMessages.approvalRequired)}</span>
        <strong>{item.title}</strong>
      </header>
      <ProposalPreview item={item} />
      <p className={styles.approvalKind}>
        {formatMessage(
          isNew
            ? patientAgentMessages.approvalDiffNewTemplate
            : patientAgentMessages.approvalDiffNewVersion,
        )}
      </p>
      {resolved ? (
        <p className={styles.approvalStatus}>
          {formatMessage(
            item.status === 'approved'
              ? patientAgentMessages.approvalApproved
              : item.status === 'feedback'
                ? patientAgentMessages.approvalFeedbackSent
                : patientAgentMessages.approvalDisapproved,
          )}
        </p>
      ) : feedbackOpen ? (
        <div className={styles.approvalFeedback}>
          <Textarea
            value={feedback}
            rows={2}
            autoFocus
            placeholder={formatMessage(
              patientAgentMessages.approvalFeedbackPlaceholder,
            )}
            onChange={(event) => setFeedback(event.target.value)}
          />
          <div className={styles.approvalOptions}>
            <button
              type="button"
              className={styles.approvalOption}
              disabled={disabled || !feedback.trim()}
              onClick={() =>
                onDecision(item.proposalID, 'feedback', feedback.trim())
              }
            >
              {formatMessage(patientAgentMessages.approvalSendFeedback)}
            </button>
            <button
              type="button"
              className={styles.approvalOption}
              disabled={disabled}
              onClick={closeFeedback}
            >
              {formatMessage(patientAgentMessages.approvalFeedbackBack)}
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.approvalOptions}>
          <button
            type="button"
            className={cn(styles.approvalOption, styles.approvalOptionApprove)}
            disabled={disabled}
            onClick={() => onDecision(item.proposalID, 'approve')}
          >
            {formatMessage(patientAgentMessages.approvalApprove)}
          </button>
          <button
            type="button"
            className={cn(
              styles.approvalOption,
              styles.approvalOptionDisapprove,
            )}
            disabled={disabled}
            onClick={() => onDecision(item.proposalID, 'disapprove')}
          >
            {formatMessage(patientAgentMessages.approvalDisapprove)}
          </button>
          <button
            type="button"
            className={styles.approvalOption}
            disabled={disabled}
            onClick={() => setFeedbackOpen(true)}
          >
            {formatMessage(patientAgentMessages.approvalSendFeedback)}
          </button>
        </div>
      )}
    </section>
  );
};
